import { AxiosResponse } from "axios";
import { useContext } from "react";
import {
  IInvoiceExcelDownloadJobRegistrationRequest,
  IInvoiceExcelDownloadJobRegistrationRequestV2,
  IJobRegistrationResponse,
  IJobStatusResponse,
  InvoiceByAccountWithFiltersConsolidationRequest,
  InvoiceByAccountWithNumbersConsolidationRequest,
  InvoiceByFiltersConsolidationRequest,
  InvoiceByNumbersConsolidationRequest,
  IReturnsExcelDownloadJobRegistrationRequest,
  IReturnSummaryExcelDownloadJobRegistrationRequest,
  IStatusRequest,
} from "./IJobsService";
import { ApiContext } from "../../context/ApiContext/ApiContext";
import { ConfigContext } from "../../context/ConfigContext";
import {
  ALL_ACCOUNTS,
  INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION,
  INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION,
  INVOICE_CONSOLIDATION,
  INVOICE_EXCEL,
  INVOICE_FILTER,
  RETURN_SUMMARY_EXCEL,
  RETURNS_EXCEL,
} from "../../utils/constants";

const useJobServiceApi = () => {
  const axios = useContext(ApiContext);
  const { JOBS_SERVICE_URL: jobsServiceUrl } = useContext(ConfigContext);
  return {
    invokeInvoiceByAccountWithNumbersDownload: async (
      // Multiple Accounts, Invoices individually selected (CPM only)
      request: InvoiceByAccountWithNumbersConsolidationRequest,
      xid: string,
      location: string,
      lang?: string,
      enableInvoicesViewed?: boolean,
      mru?: string,
      format?: "zip" | "pdf"
    ): Promise<AxiosResponse<IJobRegistrationResponse>> => {
      const filename = "Consolidated_Invoices";
      const type = INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION;
      const accountIds = request.accountIdToInvoiceNumbers.map(
        (account) => account.accountId
      );
      const payload = {
        filename,
        format: format || "zip", // default to zip for multiple accounts
        ...request,
        xid,
        location,
        mru,
        lang,
      };
      const params = { lang };
      const accountIdData = {
        accountId: accountIds ? ALL_ACCOUNTS : accountIds,
      };

      return axios
        .postWithRetries(
          `${jobsServiceUrl}/jobs/v1/register/invoice-by-account-with-numbers-download?enableInvoicesRead=${enableInvoicesViewed}`,
          payload,
          { params }
        )
        .then((response) => ({
          ...response,
          data: {
            ...response.data,
            ...accountIdData,
            type,
          },
        }));
    },

    invokeInvoiceByAccountWithFilterDownload: async (
      // Multiple Accounts, Invoice selected via filter (CPM only)
      request: InvoiceByAccountWithFiltersConsolidationRequest,
      xid: string,
      location: string,
      lang?: string,
      enableInvoicesViewed?: boolean,
      mru?: string,
      format?: "zip" | "pdf"
    ): Promise<AxiosResponse<IJobRegistrationResponse>> => {
      const filename = "Consolidated_Invoices";
      const type = INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION;
      const { accountIds } = request;
      const payload = {
        filename,
        format: format || "zip", // default to zip for multiple accounts
        ...request,
        enableInvoicesRead: enableInvoicesViewed,
        xid,
        location,
        mru,
        lang,
      };
      const params = { lang };
      const accountIdData = {
        accountId: accountIds ? ALL_ACCOUNTS : request.accountIds,
      };

      return axios
        .postWithRetries(
          `${jobsServiceUrl}/jobs/v1/register/invoice-by-account-with-filters-download`,
          payload,
          { params }
        )
        .then((response) => ({
          ...response,
          data: {
            ...response.data,
            ...accountIdData,
            type,
          },
        }));
    },

    invokeInvoiceByFilterDownload: async (
      // Single Account, Invoices selected via filter
      request: InvoiceByFiltersConsolidationRequest,
      xid: string,
      location: string,
      lang: string | undefined,
      enableInvoicesViewed?: boolean,
      mru?: string
    ): Promise<AxiosResponse<IJobRegistrationResponse>> => {
      const filename = `${request.accountId}_CONSOLIDATED_INVOICES`;
      const type = INVOICE_FILTER;
      const format = "pdf";
      const payload = {
        filename,
        format,
        ...request,
        xid,
        location,
        mru,
        lang,
      };
      const params = { lang };
      const accountIdData = { accountId: request.accountId };

      const response = await axios.postWithRetries(
        `${jobsServiceUrl}/jobs/v1/register/invoices-by-filter-download?enableInvoicesRead=${enableInvoicesViewed}`,
        payload,
        { params }
      );
      return {
        ...response,
        data: {
          ...response.data,
          ...accountIdData,
          type,
        },
      };
    },

    invokeInvoiceByNumbersConsolidationDownload: async (
      // Single account, Invoices individually selected
      request: InvoiceByNumbersConsolidationRequest,
      xid: string,
      location: string,
      lang?: string,
      enableInvoicesViewed?: boolean,
      mru?: string
    ): Promise<AxiosResponse<IJobRegistrationResponse>> => {
      const filename = `${request.accountId}_CONSOLIDATED_INVOICES`;
      const type = INVOICE_CONSOLIDATION;
      const format = "pdf";
      const payload = {
        filename,
        format,
        ...request,
        xid,
        location,
        mru,
        lang,
      };
      const params = { lang };
      const accountIdData = { accountId: request.accountId };

      const response = await axios.postWithRetries(
        `${jobsServiceUrl}/jobs/v1/register/invoice-by-numbers-consolidation?enableInvoicesRead=${enableInvoicesViewed}`,
        payload,
        { params }
      );
      return {
        ...response,
        data: {
          ...response.data,
          ...accountIdData,
          type,
        },
      };
    },

    getStatuses: async (
      request: IStatusRequest,
      accountId: string
    ): Promise<IJobStatusResponse> => {
      const response = await axios.postWithRetries(
        `${jobsServiceUrl}/jobs/v1/statuses`,
        request,
        {
          params: {
            accountId,
          },
        }
      );
      return response.data;
    },

    invokeInvoiceExcelDownload: async (
      request: IInvoiceExcelDownloadJobRegistrationRequest,
      lang?: string
    ): Promise<AxiosResponse<IJobRegistrationResponse>> =>
      axios
        .postWithRetries(
          `${jobsServiceUrl}/jobs/v1/register/invoice-excel-download`,
          {
            accountId: request.accountId,
            withHandlingUnits: request.withHandlingUnits,
            withOfficialDocumentNumbers: request.withOfficialDocumentNumbers,
            dealerLocation: request.dealerLocation,
            withLeadingZeros: request.withLeadingZeros,
            lang,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
        .then((response) => ({
          ...response,
          data: {
            ...response.data,
            accountId: request.accountId,
            type: INVOICE_EXCEL,
          },
        })),

    invokeInvoiceExcelDownloadV2: async (
      request: IInvoiceExcelDownloadJobRegistrationRequestV2,
      lang?: string
    ): Promise<AxiosResponse<IJobRegistrationResponse>> =>
      axios
        .postWithRetries(
          `${jobsServiceUrl}/jobs/v2/register/invoice-excel-download`,
          {
            accountIds: request.accountIds,
            withHandlingUnits: request.withHandlingUnits,
            withOfficialDocumentNumbers: request.withOfficialDocumentNumbers,
            dealerLocation: request.dealerLocation,
            withLeadingZeros: request.withLeadingZeros,
            lang,
            invoiceNumber: request.invoiceNumber,
            invoiceType: request.invoiceType,
            srcProcessType: request.srcProcessType,
            nfeNumber: request.nfeNumber,
            documentNumber: request.documentNumber,
            huNumber: request.huNumber,
            dealerReference: request.dealerReference,
            officialDocumentNumber: request.officialDocumentNumber,
            partNumber: request.partNumber,
            startDate: request.startDate,
            endDate: request.endDate,
            dealerId: request.dealerId,
            factura: request.factura,
            sortField: request.sortField,
            sortDir: request.sortDir,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
        .then((response) => ({
          ...response,
          data: {
            ...response.data,
            accountIds: request.accountIds,
            type: INVOICE_EXCEL,
          },
        })),

    downloadJob: async (
      jobId: string,
      accountId: string,
      filename: string,
      lang?: string
    ): Promise<AxiosResponse<Blob>> =>
      axios
        .getWithRetries(`${jobsServiceUrl}/jobs/v1/${jobId}/download`, {
          responseType: "blob",
          params: { accountId, filename, lang },
        })
        .then((response) => response),

    invokeReturnsExcelDownload: async (
      request: IReturnsExcelDownloadJobRegistrationRequest,
      lang?: string
    ): Promise<AxiosResponse<IJobRegistrationResponse>> =>
      axios
        .postWithRetries(
          `${jobsServiceUrl}/jobs/v1/register/returns-excel-download`,
          { lang, criteria: request },
          { headers: { "Content-Type": "application/json" } }
        )
        .then((response) => ({
          ...response,
          data: {
            ...response.data,
            accountIds: request.accountIds,
            type: RETURNS_EXCEL,
          },
        })),

    invokeReturnSummaryExcelDownload: async (
      request: IReturnSummaryExcelDownloadJobRegistrationRequest,
      lang?: string
    ): Promise<AxiosResponse<IJobRegistrationResponse>> =>
      axios
        .postWithRetries(
          `${jobsServiceUrl}/jobs/v1/register/return-summary-excel-download`,
          { lang, ...request },
          { headers: { "Content-Type": "application/json" } }
        )
        .then((response) => ({
          ...response,
          data: {
            ...response.data,
            accountId: request.accountId,
            type: RETURN_SUMMARY_EXCEL,
          },
        })),
  };
};

export default useJobServiceApi;
